import { OverwriteTimeOption } from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { RadioButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/RadioButton';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { VisitType } from '../../../calendar/visit/common/types';
import { FieldBlock } from '../../../common/FieldBlock';
import { TimePicker } from '../../../common/ui-kit/inputs/date-time/TimePicker';
import { DropDownInput } from '../../../common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '../../../common/ui-kit/inputs/Input';

import styles from './../SettingsBlock.module.scss';
import { AdditionalVisitTable } from './AdditionalVisitTable';
import { HL7SettingsFormProps } from './types';
import { isEmpty } from '../../../measurements/copy-datablock-field/common';
import { useEventCallback } from '@mui/material';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';

export const VisitCreatingSettings: FC<HL7SettingsFormProps> = (props) => {
  const i18next = useScopedTranslation('tenant_settings.hl7.incoming');
  const i18nextGlobal = useTranslation();

  const visitTypes = useActiveVisitTypes();
  const translatedVisitTypes: VisitType[] = useMemo(
    () =>
      visitTypes.map((type) => ({
        id: type.id,
        label: i18nextGlobal.t(type.value as any),
        specificStartTimeInSecondsSinceMidnight:
          type.specificStartTimeInSecondsSinceMidnight,
        defaultDurationInSeconds: type.defaultDurationInSeconds,
      })),
    [visitTypes],
  );

  const { register, control, formState, watch, setValue } = props.form;

  const [overwriteTime, overwriteTimeOption] = watch([
    'overwriteTime',
    'overwriteTimeOption',
  ]);
  const setOverwriteTimeOption = useCallback((option: OverwriteTimeOption) => {
    setValue('overwriteTimeOption', option, { shouldDirty: true });
  }, []);

  const setVisitStartTimeAndLength = useEventCallback(
    (visitType: VisitType) => {
      if (visitType?.specificStartTimeInSecondsSinceMidnight) {
        setValue(
          'visitStartTimeInSecondsSinceMidnight',
          visitType.specificStartTimeInSecondsSinceMidnight,
          validateAndDirty,
        );
      }
      if (visitType?.defaultDurationInSeconds) {
        setValue(
          'visitLengthInHours',
          visitType.defaultDurationInSeconds / 3600,
          validateAndDirty,
        );
      }
    },
  );

  return (
    <FieldBlock
      title={i18next.t('visit_creating_settings')}
      className={clsx(styles.fieldBlock, styles.visitCreating)}
    >
      <div className={styles.row}>
        <div className={styles.column}>
          <Field title={i18next.t('visit_type')} className={styles.field}>
            <Controller
              control={control}
              name={'defaultVisitTypeId'}
              render={({ field: { onChange, value } }) => (
                <div className={styles.input}>
                  <DropDownInput
                    useIdFunctionAsValue={true}
                    options={translatedVisitTypes}
                    value={value}
                    idFunction={'id'}
                    getOptionLabel={'label'}
                    required={true}
                    onValueChanged={(value) => {
                      onChange(value?.id);
                      setVisitStartTimeAndLength(value);
                    }}
                    errorText={formState.errors.defaultVisitTypeId?.message}
                  />
                </div>
              )}
            />
          </Field>
          <Field title={''}>
            <CheckBox
              title={i18next.t('visit_creating.overwrite_time')}
              {...register('overwriteTime')}
            />
          </Field>
        </div>

        <div className={styles.column}>
          <div className={styles.searchIntervalTitle}>
            {i18next.t('visit_search_interval')}
          </div>
          <div className={styles.row}>
            <Field
              title={i18next.t('visit_search_days_backward')}
              className={styles.intervalField}
            >
              <Input
                variant="normal"
                type="number"
                {...register('visitSearchBackwardDaysCount', {
                  setValueAs: (value) => (isEmpty(value) ? undefined : value),
                })}
                errorText={
                  formState.errors.visitSearchBackwardDaysCount?.message
                }
              />
            </Field>
            <Field
              title={i18next.t('visit_search_days_forward')}
              className={clsx(styles.right, styles.intervalField)}
            >
              <Input
                variant="normal"
                type="number"
                {...register('visitSearchForwardDaysCount', {
                  setValueAs: (value) => (isEmpty(value) ? undefined : value),
                })}
                errorText={
                  formState.errors.visitSearchForwardDaysCount?.message
                }
              />
            </Field>
          </div>
        </div>
        <div>
          <div className={styles.searchIntervalTitle}>
            {i18next.t('case_search_interval')}
          </div>
          <div className={styles.row}>
            <Field
              title={i18next.t('case_search_days_backward')}
              className={styles.intervalField}
            >
              <Input
                variant="normal"
                type="number"
                {...register('caseSearchBackwardDaysCount', {
                  setValueAs: (value) => (isEmpty(value) ? undefined : value),
                })}
                errorText={
                  formState.errors.caseSearchBackwardDaysCount?.message
                }
              />
            </Field>
            <Field
              title={i18next.t('case_search_days_forward')}
              className={clsx(styles.right, styles.intervalField)}
            >
              <Input
                variant="normal"
                type="number"
                {...register('caseSearchForwardDaysCount', {
                  setValueAs: (value) => (isEmpty(value) ? undefined : value),
                })}
                errorText={formState.errors.caseSearchForwardDaysCount?.message}
              />
            </Field>
          </div>
        </div>
      </div>

      {overwriteTime && (
        <>
          <div className={styles.row}>
            <Field title={''}>
              <RadioButton
                name={'overwrite_if_empty'}
                title={i18next.t('visit_creating.overwrite_end_if_empty')}
                checked={
                  overwriteTimeOption ===
                  OverwriteTimeOption.OverwriteEndIfEmpty
                }
                onChange={() =>
                  setOverwriteTimeOption(
                    OverwriteTimeOption.OverwriteEndIfEmpty,
                  )
                }
              />
            </Field>
            <Field title={''} className={styles.field}>
              <RadioButton
                name={'overwrite_always'}
                title={i18next.t('visit_creating.overwrite_always')}
                checked={
                  overwriteTimeOption === OverwriteTimeOption.OverwriteAlways
                }
                onChange={() =>
                  setOverwriteTimeOption(OverwriteTimeOption.OverwriteAlways)
                }
                className={styles.right}
              />
            </Field>
          </div>
          <div className={styles.range}>
            <Field title={i18next.t('start_time')}>
              <Controller
                control={control}
                name={'visitStartTimeInSecondsSinceMidnight'}
                render={({ field: { onChange, value } }) => (
                  <div className={styles.input}>
                    <TimePicker
                      variant="normal"
                      timeInMills={value ? value * 1000 : value}
                      error={
                        !!formState.errors.visitStartTimeInSecondsSinceMidnight
                      }
                      errorText={
                        formState.errors.visitStartTimeInSecondsSinceMidnight
                          ?.message
                      }
                      onTimeChanged={(value) =>
                        onChange(value ? value / 1000 : value)
                      }
                    />
                  </div>
                )}
              />
            </Field>
            <Field
              title={i18next.t('visit_length')}
              className={clsx(styles.field, styles.right)}
            >
              <Input
                variant="normal"
                type="number"
                step="any"
                {...register('visitLengthInHours', {
                  setValueAs: (value) => (isEmpty(value) ? undefined : value),
                })}
                errorText={formState.errors.visitLengthInHours?.message}
              />
            </Field>
          </div>
          <AdditionalVisitTable {...props} />
        </>
      )}
    </FieldBlock>
  );
};
